/**
 * homezone-store-display-locator.js
 * 
 * Handles client-side geolocation and dynamically updating the "See it on display near you"
 * widget based on the currently selected product variant.
 */

(function() {
  const LOCAL_STORAGE_KEY = 'homezone_customer_location';
  let state = {
    customerLocation: null, // { zip, lat, lng, city, timestamp, isGuess }
    currentVariantId: null,
    stores: [],
    variantData: {}
  };

  const EXCLUDED_OPTION_NAMES = [
    'size', 
    'case pieces', 
    'chair configuration', 
    'configuration', 
    'chaise arrangement',
    'sectional arrangement',
    'arrangement',
    'pieces', 
    'foundation', 
    'base', 
    'set type', 
    'box spring', 
    'set option',
    'mattress option',
    'add foundation'
  ];

  function getCleanVariantTitle(variant) {
    if (!variant.options || variant.options.length === 0) {
      return variant.title === 'Default Title' ? '' : variant.title;
    }
    
    // Filter out excluded options
    const cleanOptions = variant.options.filter(opt => {
      if (!opt.name) return true;
      const nameLower = opt.name.toLowerCase().trim();
      return !EXCLUDED_OPTION_NAMES.some(excluded => nameLower === excluded || nameLower.includes(excluded));
    });
    
    // If we have remaining options, join their values
    if (cleanOptions.length > 0) {
      return cleanOptions.map(opt => opt.value).join(' / ');
    }
    
    // If everything was filtered out, return empty string so we don't display size/pieces.
    return '';
  }

  // ---------------------------------------------------------------------------
  // HELPER: NORMALIZATION
  // ---------------------------------------------------------------------------

  function normalizeLocationName(name) {
    if (!name) return '';
    let s = name.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
    if (s === 'north lewisville') return 'lewisville north';
    if (s === 'south lewisville') return 'lewisville south';
    return s;
  }

  // ---------------------------------------------------------------------------
  // GEOLOCATION PROVIDERS (Architected for easy swapping)
  // ---------------------------------------------------------------------------

  /**
   * Provider 1: IP Geolocation Guess
   * Currently uses GeoJS. Swappable to Cloudflare, BigDataCloud, etc.
   * Returns a promise resolving to { zip, lat, lng, city } or null.
   */
  async function fetchLocationFromIP() {
    try {
      // Future swap: Replace URL and mapping logic for another provider.
      const response = await fetch('https://get.geojs.io/v1/ip/geo.json');
      if (!response.ok) return null;
      const data = await response.json();
      
      if (data.latitude && data.longitude) {
        return {
          zip: data.postal || '', // Not all IP APIs return a zip accurately
          city: data.city || '',
          lat: parseFloat(data.latitude),
          lng: parseFloat(data.longitude),
          isGuess: true
        };
      }
    } catch (e) {
      console.warn('IP Geolocation failed:', e);
    }
    return null;
  }

  /**
   * Provider 2: Geocoding (ZIP -> Coordinates)
   * Currently uses OpenStreetMap Nominatim. Swappable to Google Maps, Mapbox, etc.
   * Includes a fallback to local store DB ZIP matching.
   * Returns a promise resolving to { zip, lat, lng, city } or null.
   */
  async function geocodeZipCode(zipCode) {
    // 1. Try an external geocoding service (Nominatim) restricted to US.
    try {
      const query = encodeURIComponent(`${zipCode}`);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

      const response = await fetch(`https://nominatim.openstreetmap.org/search?postalcode=${query}&country=US&format=json&limit=1`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          const parts = data[0].display_name.split(',');
          const city = parts.length > 0 ? parts[0].trim() : '';

          return {
            zip: zipCode,
            city: city,
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon),
            isGuess: false
          };
        }
      }
    } catch (e) {
      console.warn('Geocoding API failed:', e);
    }

    // 2. Fallback: Check if the zip matches any of our known store zips directly
    const matchingStore = state.stores.find(s => s.zip && s.zip.trim() === zipCode.trim());
    if (matchingStore && matchingStore.lat && matchingStore.lng) {
      return {
        zip: zipCode,
        city: matchingStore.city || '',
        lat: parseFloat(matchingStore.lat),
        lng: parseFloat(matchingStore.lng),
        isGuess: false
      };
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // DATA MANAGEMENT & CACHING
  // ---------------------------------------------------------------------------

  function loadCachedLocation() {
    try {
      const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed;
      }
    } catch (e) {
      console.warn('Failed to load location from cache:', e);
    }
    return null;
  }

  function saveLocationToCache(locationObj) {
    try {
      locationObj.timestamp = Date.now();
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(locationObj));
      state.customerLocation = locationObj;
    } catch (e) {
      console.warn('Failed to save location to cache:', e);
    }
  }

  // ---------------------------------------------------------------------------
  // DISTANCE MATH
  // ---------------------------------------------------------------------------

  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3958.8; // Radius of the Earth in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    return R * c;
  }

  // ---------------------------------------------------------------------------
  // HELPER: OPTION TYPE CHECK & DROPDOWNS
  // ---------------------------------------------------------------------------

  function hasColorOrReclinerTypeOptions() {
    for (const id in state.variantData) {
      const variant = state.variantData[id];
      if (variant.options) {
        for (const opt of variant.options) {
          if (opt.name) {
            const nameLower = opt.name.toLowerCase().trim();
            if (nameLower === 'color' || nameLower === 'recliner type' || nameLower.includes('color') || nameLower.includes('recliner type')) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  function syncOptionToPage(optionName, optionValue) {
    const variantSelects = document.querySelector('variant-selects');
    const variantRadios = document.querySelector('variant-radios');
    const container = variantSelects || variantRadios || document;
    
    // Find select element
    const selects = container.querySelectorAll('select');
    for (const select of selects) {
      const hasValue = Array.from(select.options).some(opt => opt.value === optionValue);
      if (hasValue) {
        if (select.value !== optionValue) {
          select.value = optionValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }

    // Find radio input
    const inputs = container.querySelectorAll('input[type="radio"]');
    for (const input of inputs) {
      if (input.value === optionValue || input.getAttribute('data-value') === optionValue) {
        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }
  }

  function findBestMatchingVariant(selectedOptions, changedOptionName) {
    let bestMatch = null;
    let maxMatches = -1;

    for (const id in state.variantData) {
      const variant = state.variantData[id];
      let matches = 0;
      let matchesChangedOption = true;

      for (const opt of variant.options) {
        if (selectedOptions[opt.name] === opt.value) {
          matches++;
        } else if (opt.name === changedOptionName) {
          matchesChangedOption = false;
        }
      }

      if (matchesChangedOption) {
        if (matches > maxMatches) {
          maxMatches = matches;
          bestMatch = id;
        }
      }
    }
    return bestMatch || Object.keys(state.variantData)[0];
  }

  function setupOptionDropdowns(container) {
    const optionsContainer = container.querySelector('.homezone-display-locator__options');
    if (!optionsContainer) return;
    optionsContainer.innerHTML = '';

    const isShowroomsMode = container.getAttribute('data-mode') === 'showrooms';

    const isColorOrRecliner = !isShowroomsMode && hasColorOrReclinerTypeOptions();
    if (!isColorOrRecliner) {
      optionsContainer.style.display = 'none';
      return;
    }

    optionsContainer.style.display = 'flex';

    // Add "See it in" label
    const label = document.createElement('span');
    label.className = 'homezone-locator-options-label';
    label.textContent = 'See it in ';
    optionsContainer.appendChild(label);

    // 1. Identify which options are Color or Recliner Type and extract unique values
    const optionNamesToRender = [];
    const optionValuesMap = {};

    for (const id in state.variantData) {
      const variant = state.variantData[id];
      if (variant.options) {
        variant.options.forEach(opt => {
          if (opt.name) {
            const nameLower = opt.name.toLowerCase().trim();
            if (nameLower === 'color' || nameLower === 'recliner type' || nameLower.includes('color') || nameLower.includes('recliner type')) {
              if (!optionValuesMap[opt.name]) {
                optionValuesMap[opt.name] = new Set();
                optionNamesToRender.push(opt.name);
              }
              optionValuesMap[opt.name].add(opt.value);
            }
          }
        });
      }
    }

    // 2. Create dropdowns
    optionNamesToRender.forEach(optName => {
      const select = document.createElement('select');
      select.className = 'homezone-locator-select';
      select.setAttribute('data-option-name', optName);
      select.setAttribute('aria-label', `Select ${optName}`);

      const uniqueValues = Array.from(optionValuesMap[optName]);
      uniqueValues.forEach(val => {
        const option = document.createElement('option');
        option.value = val;
        option.textContent = val;
        select.appendChild(option);
      });

      // Set initial value
      const currentVariant = state.variantData[state.currentVariantId];
      if (currentVariant && currentVariant.options) {
        const matchingOpt = currentVariant.options.find(o => o.name === optName);
        if (matchingOpt) {
          select.value = matchingOpt.value;
        }
      }

      // Handle selection change
      select.addEventListener('change', () => {
        const selectedOptions = {};
        const allSelects = optionsContainer.querySelectorAll('.homezone-locator-select');
        allSelects.forEach(s => {
          selectedOptions[s.getAttribute('data-option-name')] = s.value;
        });

        const targetVariantId = findBestMatchingVariant(selectedOptions, optName);
        const targetVariant = state.variantData[targetVariantId];

        if (targetVariant) {
          // 1. Temporarily disable all programmatic scroll functions to block theme's native jump/scroll behavior
          const originalScrollTo = window.scrollTo;
          const originalScroll = window.scroll;
          const originalScrollBy = window.scrollBy;
          const originalScrollIntoView = Element.prototype.scrollIntoView;

          window.scrollTo = function() {};
          window.scroll = function() {};
          window.scrollBy = function() {};
          Element.prototype.scrollIntoView = function() {};

          // 2. Sync options back to the main product page
          if (targetVariant.options) {
            targetVariant.options.forEach(o => {
              syncOptionToPage(o.name, o.value);
            });
          }
          state.currentVariantId = targetVariantId;
          renderAllLocators();

          // 3. Restore programmatic scroll functions after a delay, letting theme JS finish execution
          setTimeout(() => {
            window.scrollTo = originalScrollTo;
            window.scroll = originalScroll;
            window.scrollBy = originalScrollBy;
            Element.prototype.scrollIntoView = originalScrollIntoView;
          }, 500);
        }
      });

      optionsContainer.appendChild(select);
    });
  }

  // ---------------------------------------------------------------------------
  // UI & RENDER LOGIC
  // ---------------------------------------------------------------------------

  function renderShowroomMode(container) {
    container.classList.remove('hidden');

    const textEl = container.querySelector('.homezone-display-locator__text-row');
    const labelEl = container.querySelector('#HomeZoneZipLabel');
    if (!textEl) return;

    // Calculate distances to all active stores first
    let closestStoreOverall = null;
    let minDistance = Infinity;

    if (state.customerLocation && state.customerLocation.lat && state.customerLocation.lng) {
      state.stores.forEach(store => {
        if (store.lat && store.lng) {
          store.distance = calculateDistance(
            state.customerLocation.lat, 
            state.customerLocation.lng, 
            store.lat, 
            store.lng
          );
          if (store.distance < minDistance) {
            minDistance = store.distance;
            closestStoreOverall = store;
          }
        } else {
          store.distance = Infinity;
        }
      });
    }

    if (closestStoreOverall) {
      const storeName = closestStoreOverall.name || closestStoreOverall.locationKey;
      const mapUrl = closestStoreOverall.url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(storeName + ' ' + (closestStoreOverall.address || '') + ' ' + (closestStoreOverall.city || ''))}`;
      const storeLink = `<a href="${mapUrl}" target="_blank" class="homezone-display-locator__store-link">${storeName}</a>`;

      if (state.customerLocation.isGuess) {
        textEl.innerHTML = `Your closest Home Zone appears to be ${storeLink}.`;
        if (labelEl) labelEl.textContent = 'Enter your ZIP code to confirm:';
      } else {
        textEl.innerHTML = `Your closest Home Zone is ${storeLink}.`;
        if (labelEl) labelEl.textContent = 'Enter your ZIP code to update:';
      }
    } else {
      textEl.innerHTML = 'Find a Home Zone Furniture showroom to see fabric samples.';
      if (labelEl) labelEl.textContent = 'Enter your ZIP code to find showrooms:';
    }
  }

  function renderStores(container) {
    if (!container) return;

    const isShowroomsMode = container.getAttribute('data-mode') === 'showrooms';
    if (isShowroomsMode) {
      renderShowroomMode(container);
      return;
    }

    const listContainer = container.querySelector('.homezone-display-locator__content');
    const titleEl = container.querySelector('.homezone-display-locator__title');
    const guessEl = container.querySelector('.homezone-display-locator__subtitle');
    const closestStoreEl = container.querySelector('.homezone-display-locator__closest-store');
    const linkWrapperEl = document.getElementById('HomeZoneDisplayLocatorLinkWrapper');
    
    if (!listContainer) return;
    const isColorOrRecliner = hasColorOrReclinerTypeOptions();

    let variantLocationsRaw = [];
    let otherVariantsWithLocations = [];

    if (!isColorOrRecliner) {
      // Look at all variants to pool locations
      const allLocs = new Set();
      for (const id in state.variantData) {
        const locs = state.variantData[id].locations || [];
        locs.forEach(l => allLocs.add(l));
      }
      variantLocationsRaw = Array.from(allLocs);
    } else {
      // Pool locations across all variants that share the same clean title (e.g., same Color / Recliner Type)
      // so Chaise/Sectional Arrangement or Size differences don't split display availability.
      const currentVariantInfo = state.variantData[state.currentVariantId];
      const currentCleanTitle = currentVariantInfo ? getCleanVariantTitle(currentVariantInfo) : '';

      const matchingLocs = new Set();
      for (const [id, data] of Object.entries(state.variantData)) {
        const cleanTitle = getCleanVariantTitle(data);
        if (id === state.currentVariantId || (currentCleanTitle && cleanTitle === currentCleanTitle)) {
          (data.locations || []).forEach(l => matchingLocs.add(l));
        }
      }
      variantLocationsRaw = Array.from(matchingLocs);

      if (variantLocationsRaw.length === 0) {
        for (const [id, data] of Object.entries(state.variantData)) {
          if (data.locations && data.locations.length > 0 && id !== state.currentVariantId) {
            otherVariantsWithLocations.push(data);
          }
        }
      }
    }

    if (!isShowroomsMode && variantLocationsRaw.length === 0 && otherVariantsWithLocations.length === 0) {
      container.classList.add('hidden');
      if (linkWrapperEl) linkWrapperEl.classList.add('hidden');
      return;
    }

    container.classList.remove('hidden');
    if (linkWrapperEl) linkWrapperEl.classList.remove('hidden');

    // Update our custom dropdowns to match the new current variant
    const currentVariant = state.variantData[state.currentVariantId];
    if (currentVariant && currentVariant.options) {
      currentVariant.options.forEach(opt => {
        const select = container.querySelector(`.homezone-locator-select[data-option-name="${opt.name}"]`);
        if (select && select.value !== opt.value) {
          select.value = opt.value;
        }
      });
    }

    // 1. Calculate distances to ALL active stores first
    let closestStoreOverall = null;
    let minDistance = Infinity;

    if (state.customerLocation && state.customerLocation.lat && state.customerLocation.lng) {
      state.stores.forEach(store => {
        if (store.lat && store.lng) {
          store.distance = calculateDistance(
            state.customerLocation.lat, 
            state.customerLocation.lng, 
            store.lat, 
            store.lng
          );
          if (store.distance < minDistance) {
            minDistance = store.distance;
            closestStoreOverall = store;
          }
        } else {
          store.distance = Infinity;
        }
      });
    }

    // 2. Filter matching variant locations
    let matchingStores = [];
    let locationsToMatch = variantLocationsRaw;
    let isOtherVariants = false;
    let variantNames = [];

    if (isShowroomsMode) {
      matchingStores = [...state.stores];
    } else {
      if (variantLocationsRaw.length === 0 && otherVariantsWithLocations.length > 0) {
        isOtherVariants = true;
        let allOtherLocs = new Set();
        let titles = new Set();
        otherVariantsWithLocations.forEach(v => {
          v.locations.forEach(l => allOtherLocs.add(l));
          const cleanTitle = getCleanVariantTitle(v);
          if (cleanTitle) {
            titles.add(cleanTitle);
          }
        });
        locationsToMatch = Array.from(allOtherLocs);
        variantNames = Array.from(titles);
      }

      const normalizedVariantLocations = locationsToMatch.map(normalizeLocationName);
      matchingStores = state.stores.filter(store => {
        if (!store.locationKey) return false;
        return normalizedVariantLocations.includes(normalizeLocationName(store.locationKey));
      });
    }

    // 3. Sort matching stores by distance (closest first)
    if (state.customerLocation) {
      matchingStores.sort((a, b) => a.distance - b.distance);
    }

    // Grab top 3
    const topStores = matchingStores.slice(0, 3);
    listContainer.innerHTML = '';

    if (topStores.length > 0) {
      if (isShowroomsMode) {
        titleEl.innerText = "Showrooms near you";
      } else if (isOtherVariants && variantNames.length > 0) {
        let titleText = "Available to view in " + variantNames.join(', ') + " at";
        if (variantNames.length === 2) {
          titleText = "Available to view in " + variantNames.join(' or ') + " at";
        } else if (variantNames.length > 2) {
          const last = variantNames.pop();
          titleText = "Available to view in " + variantNames.join(', ') + ", or " + last + " at";
        }
        titleEl.innerText = titleText;
      } else {
        titleEl.innerText = "See it on display near you";
      }
      
      topStores.forEach(store => {
        const card = document.createElement(store.url ? 'a' : 'div');
        card.className = 'homezone-store-card';
        if (store.url) {
          card.href = store.url;
          card.target = "_blank";
        }

        let distanceHtml = '';
        if (store.distance && store.distance !== Infinity && state.customerLocation && !state.customerLocation.isGuess) {
          distanceHtml = `<div class="homezone-store-card__distance">${store.distance.toFixed(1)} mi</div>`;
        }

        const statusText = isShowroomsMode ? 'Showroom' : 'On Display';
        card.innerHTML = `
          <div class="homezone-store-card__info">
            <h4 class="homezone-store-card__name">${store.name || store.locationKey}</h4>
            <span class="homezone-store-card__status">
              <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5L4.5 9L13 1" stroke="#279f6e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              ${statusText}
            </span>
          </div>
          ${distanceHtml}
        `;
        listContainer.appendChild(card);
      });
    } else {
      if (isShowroomsMode) {
        titleEl.innerText = "Visit a showroom near you";
        listContainer.innerHTML = `<p class="homezone-display-locator__fallback">Please visit our store locator page to find our showroom locations.</p>`;
      } else {
        titleEl.innerText = "This item may not be on display near you";
        if (isOtherVariants && variantNames.length > 0) {
          listContainer.innerHTML = `<p class="homezone-display-locator__fallback">It is currently on display in ${variantNames.join(', ')} at: ${locationsToMatch.join(', ')}.</p>`;
        } else {
          listContainer.innerHTML = `<p class="homezone-display-locator__fallback">It is currently on display at: ${locationsToMatch.join(', ')}.</p>`;
        }
      }
    }
    
    // 4. Update UI messaging based on distance overall
    if (guessEl) guessEl.style.display = 'none';
    if (closestStoreEl) closestStoreEl.style.display = 'none';

    if (state.customerLocation) {
      if (closestStoreOverall) {
        if (state.customerLocation.isGuess) {
          if (closestStoreEl) {
            closestStoreEl.innerText = `We think your closest Home Zone store is ${closestStoreOverall.name || closestStoreOverall.locationKey}. Enter your ZIP code to confirm.`;
            closestStoreEl.style.display = 'block';
          } else if (guessEl) {
            guessEl.innerText = `Your closest Home Zone appears to be ${closestStoreOverall.name || closestStoreOverall.locationKey}. Enter your ZIP code to confirm or update.`;
            guessEl.style.display = 'block';
          }
        } else {
          if (guessEl) {
            guessEl.innerText = `Displaying stores near ${state.customerLocation.zip || state.customerLocation.city}.`;
            guessEl.style.display = 'block';
          }
        }
      }
    } else {
      // Fallback text if lookup fails and we have no customer location
      if (locationsToMatch.length > 0) {
        if (isOtherVariants && variantNames.length > 0) {
          guessEl.innerText = `This item is on display in ${variantNames.join(', ')} at these Home Zone stores: ${locationsToMatch.join(', ')}. Enter your ZIP code to sort by distance.`;
        } else {
          guessEl.innerText = `This item is on display at these Home Zone stores: ${locationsToMatch.join(', ')}. Enter your ZIP code to sort by distance.`;
        }
        guessEl.style.display = 'block';
      }
    }
  }

  function renderAllLocators() {
    const containers = document.querySelectorAll('.homezone-display-locator');
    containers.forEach(container => {
      renderStores(container);
    });
  }

  function setupAllOptionDropdowns() {
    const containers = document.querySelectorAll('.homezone-display-locator');
    containers.forEach(container => {
      setupOptionDropdowns(container);
    });
  }

  function syncAllZipInputs(zipCode) {
    const inputs = document.querySelectorAll('.homezone-display-locator__zip-input');
    inputs.forEach(input => {
      input.value = zipCode;
    });
  }

  function bindZipForm(container) {
    const zipBtn = container.querySelector('.homezone-display-locator__zip-btn');
    const zipInput = container.querySelector('.homezone-display-locator__zip-input');
    const errorEl = container.querySelector('.homezone-display-locator__error');
    
    if (zipBtn && zipInput) {
      // Pre-fill input if we have a non-guessed ZIP
      if (state.customerLocation && !state.customerLocation.isGuess && state.customerLocation.zip) {
        zipInput.value = state.customerLocation.zip;
      }

      zipBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const zip = zipInput.value.trim();
        if (!zip) return;
        
        // Prevent repeated calls for the same zip
        if (state.customerLocation && state.customerLocation.zip === zip && !state.customerLocation.isGuess) {
          return;
        }

        const originalContent = zipBtn.innerHTML;
        zipBtn.disabled = true;
        zipBtn.innerHTML = '<span style="font-size:12px;">...</span>';
        if (errorEl) errorEl.style.display = 'none';

        try {
          const result = await geocodeZipCode(zip);
          if (result) {
            saveLocationToCache(result);
            syncAllZipInputs(result.zip);
            renderAllLocators();
          } else {
            if (errorEl) {
              errorEl.innerText = "We couldn't locate that ZIP code. Please try another.";
              errorEl.style.display = 'block';
            }
          }
        } catch (err) {
          console.error("Geocoding or rendering failed:", err);
          if (errorEl) {
            errorEl.innerText = "An error occurred. Please try again.";
            errorEl.style.display = 'block';
          }
        } finally {
          zipBtn.disabled = false;
          zipBtn.innerHTML = originalContent;
        }
      });
      
      zipInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          zipBtn.click();
        }
      });
    }
  }

  // ---------------------------------------------------------------------------
  // INITIALIZATION & EVENT LISTENERS
  // ---------------------------------------------------------------------------

  async function init() {
    // 1. Load Data from Liquid inject
    if (!window.HomeZoneStoreLocatorData) return;
    
    state.stores = window.HomeZoneStoreLocatorData.stores || [];
    state.variantData = window.HomeZoneStoreLocatorData.variantData || {};
    state.currentVariantId = window.HomeZoneStoreLocatorData.initialVariantId;

    // 2. Initialize Geolocation
    state.customerLocation = loadCachedLocation();
    
    if (!state.customerLocation) {
      // No cache, attempt IP guess
      const ipLocation = await fetchLocationFromIP();
      if (ipLocation) {
        state.customerLocation = ipLocation; 
      }
    }

    // 3. Initial Render & Form Binding
    setupAllOptionDropdowns();
    renderAllLocators();

    const containers = document.querySelectorAll('.homezone-display-locator');
    containers.forEach(container => {
      bindZipForm(container);
    });

    // 4. Subscribe to Variant Changes (Dawn Theme implementation)
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.variantChange, (eventData) => {
        if (eventData && eventData.data && eventData.data.variant) {
          state.currentVariantId = eventData.data.variant.id.toString();
          renderAllLocators();
        }
      });
    } else {
      document.addEventListener('variant-change', (e) => {
         if (e.detail && e.detail.variant) {
           state.currentVariantId = e.detail.variant.id.toString();
           renderAllLocators();
         }
      });
      
      let lastUrl = location.href; 
      new MutationObserver(() => {
        const url = location.href;
        if (url !== lastUrl) {
          lastUrl = url;
          const match = url.match(/variant=([0-9]+)/);
          if (match && match[1] && match[1] !== state.currentVariantId) {
            state.currentVariantId = match[1];
            renderAllLocators();
          }
        }
      }).observe(document, {subtree: true, childList: true});
    }

    // 5. Handle locator link clicks (Update URL hash and scroll smoothly to the locator with offset for header)
    document.addEventListener('click', (e) => {
      const link = e.target.closest('.homezone-locator-link');
      if (link) {
        e.preventDefault();
        history.pushState(null, null, '#HomeZoneDisplayLocator');
        const target = document.getElementById('HomeZoneDisplayLocator');
        if (target) {
          const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
          const elementPosition = target.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerOffset - 20; // 20px extra padding
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
